import { FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { OrganisationContext, UserContext } from '../../../context';
import {
  ChannelAppTabAction,
  NewsFeedScope,
  NewsFeedType,
  OrganisationTagType,
  StorySection,
  storyCardApi,
} from '../../../services';
import {
  Filter,
  FilterValue,
  NEWS_FEED_ADMIN_COOKIE_KEY,
  cookieOptions,
  optArrItem,
  useAppDispatch,
  useAppSelector,
  useAppTabsLabel,
  useAuthorsFilter,
  useCardCategoryFilter,
  useCardStatusFilter,
  useCardTypeFilter,
  useDateRangeFilter,
  useFilter,
  useTags,
  useTagsFilter,
} from '../../../shared';
import { getNewsFeed, getPinnedFeed } from '../../../slices';

import classes from './NewsFeedAdminRightSide.module.scss';

export const NewsFeedAdminRightSide: FunctionComponent = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const { hasInternalTags, hasExternalTags } = useTags();

  const hasCategories = Boolean(useContext(OrganisationContext).organisation.categories.length);

  const [fetchAuthors] = storyCardApi.endpoints.storyCardAuthorsFiltersByChannelId.useLazyQuery();

  const { channels } = useAppSelector(({ channels }) => channels);

  const { getAppTabsLabel } = useAppTabsLabel();

  const { channelId } = useContext(UserContext).userInfo.userData;

  const { pinnedStoryId: organisationPinnedStoryId } = useContext(OrganisationContext).organisation;

  const [cookies, setCookie] = useCookies([NEWS_FEED_ADMIN_COOKIE_KEY]);

  const [filter, setFilter] = useState<FilterValue[]>(
    cookies[NEWS_FEED_ADMIN_COOKIE_KEY]?.filter ?? [
      {
        groupId: 'sections',
        groupItems: [{ id: StorySection.WEB_FEED, value: StorySection.WEB_FEED }],
      },
    ]
  );

  const channelPinnedStoryId = useMemo(
    () => channels.find(({ id }) => id === channelId)?.pinnedStoryId,
    [channelId, channels]
  );

  const { getGroupValue, getDateRangeValue } = useFilter();

  const { group: dateRangeGroup } = useDateRangeFilter({ filter });

  const { group: authorsGroup } = useAuthorsFilter({
    filter,
    fetchItemsQuery: fetchAuthors,
    fetchItemsQueryArgs: { channelId },
  });

  const { group: categoryGroup } = useCardCategoryFilter({ filter });

  const { group: internalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.INTERNAL],
  });

  const { group: externalTagsGroup } = useTagsFilter({
    filter,
    types: [OrganisationTagType.EXTERNAL],
  });

  const { group: cardTypeGroup } = useCardTypeFilter();

  const { group: cardStatusGroup } = useCardStatusFilter();

  const groups = useMemo(
    () => [
      {
        id: 'sections',
        title: t('common.sections'),
        items: [
          {
            id: StorySection.WEB_FEED,
            label: `${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.web')}`,
            value: StorySection.WEB_FEED,
          },
          {
            id: StorySection.MOBILE_FEED,
            label: `${getAppTabsLabel(ChannelAppTabAction.NEWS_FEED)} ${t('common.mobile')}`,
            value: StorySection.MOBILE_FEED,
          },
          {
            id: StorySection.PINNED_CHANNEL,
            label: `${getAppTabsLabel(ChannelAppTabAction.PINNED_STORY)} ${t('common.at-channel')}`,
            value: StorySection.PINNED_CHANNEL,
          },
          {
            id: StorySection.PINNED_ORG,
            label: `${getAppTabsLabel(ChannelAppTabAction.PINNED_STORY)} ${t(
              'common.at-organisation'
            )}`,
            value: StorySection.PINNED_ORG,
          },
        ],
      },
      ...optArrItem(hasInternalTags, internalTagsGroup),
      ...optArrItem(hasExternalTags, externalTagsGroup),
      ...optArrItem(hasCategories, categoryGroup),
      authorsGroup,
      dateRangeGroup,
      cardTypeGroup,
      cardStatusGroup,
    ],
    [
      authorsGroup,
      cardStatusGroup,
      cardTypeGroup,
      categoryGroup,
      dateRangeGroup,
      externalTagsGroup,
      getAppTabsLabel,
      hasCategories,
      hasExternalTags,
      hasInternalTags,
      internalTagsGroup,
      t,
    ]
  );

  const filterChange = useCallback(
    (filter: FilterValue[]) => {
      const commonIdProps = { filter, multiCheck: true, propToMap: 'id' };

      const authorId = getGroupValue({ ...commonIdProps, groupId: authorsGroup.id });

      const categoryId = getGroupValue({ ...commonIdProps, groupId: categoryGroup.id });

      const internalTagsId = getGroupValue({ ...commonIdProps, groupId: internalTagsGroup.id });

      const externalTagsId = getGroupValue({ ...commonIdProps, groupId: externalTagsGroup.id });

      const section = getGroupValue({ filter, groupId: 'sections' });

      const isPinnedStory = [StorySection.PINNED_ORG, StorySection.PINNED_CHANNEL].includes(
        section
      );

      const { dateRange, from, to } = getDateRangeValue({ filter, groupId: dateRangeGroup.id });

      const storyCardType = getGroupValue({ filter, groupId: cardTypeGroup.id, multiCheck: true });

      const status = getGroupValue({ filter, groupId: cardStatusGroup.id, multiCheck: true });

      const common = {
        ...(authorId?.length && { authorId }),
        ...(categoryId?.length && { categoryId }),
        ...((internalTagsId?.length || externalTagsId?.length) && {
          organisationTags: {
            internalTagsId: internalTagsId?.[0] === null ? null : internalTagsId,
            externalTagsId: externalTagsId?.[0] === null ? null : externalTagsId,
          },
        }),
        ...(section === StorySection.HORIZONTAL && {
          feedType: [NewsFeedType.HORIZONTAL_STORY_FEED],
        }),
        ...(section === StorySection.WEB_FEED && { scope: NewsFeedScope.WEB_FEED }),
        ...(section === StorySection.MOBILE_FEED && { scope: NewsFeedScope.MOBILE_FEED }),
        ...(storyCardType?.length && { storyCardType }),
        ...(status?.length && { status }),
      };

      const requestFilterToSave = {
        ...common,
        ...(isPinnedStory && { pinnedStorySection: section }),
        dateRange,
      };

      const requestFilter = { ...common, ...(from && { from }), ...(to && { to }) };

      setCookie(
        NEWS_FEED_ADMIN_COOKIE_KEY,
        { requestFilter: requestFilterToSave, filter },
        cookieOptions()
      );

      setFilter(filter);

      if (isPinnedStory) {
        const isPinnedChannel = section === StorySection.PINNED_CHANNEL;

        const storyId = (isPinnedChannel ? channelPinnedStoryId : organisationPinnedStoryId) ?? -1;

        dispatch(getPinnedFeed({ storyId, filter: requestFilter }));

        return;
      }

      dispatch(getNewsFeed({ channelId, filter: requestFilter }));
    },
    [
      authorsGroup.id,
      cardStatusGroup.id,
      cardTypeGroup.id,
      categoryGroup.id,
      channelId,
      channelPinnedStoryId,
      dateRangeGroup.id,
      dispatch,
      externalTagsGroup.id,
      getDateRangeValue,
      getGroupValue,
      internalTagsGroup.id,
      organisationPinnedStoryId,
      setCookie,
    ]
  );

  return (
    <div className={classes['news-feed-admin-right-side']}>
      <Filter filter={filter} groups={groups} onChange={filterChange} resetPageScrollOnChange />
    </div>
  );
};
